import React, { useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TableContainer from "@mui/material/TableContainer";
import Typography from '@mui/material/Typography';
import { calculateYTG, calculateYTGPercentage, copyAccountTableToClipboard, isForecastInPast, isPeriodInPast } from '../Utilities/tableUtils';
import { formatWeekHeader, isDatePeriod } from '../Utilities/weekNumberUtils';
import IconButton from '@mui/material/IconButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import './SkuBaseForecastTable.css';

const OutputsAccountTable = ({ data, startMonth, selectedForecast, aggregationPeriod = "monthly", showActuals = false, loadingActuals = false, showCompare = false }) => {
    const [showDetailedUnits, setShowDetailedUnits] = useState(false);
    const [showFixedSpendDetails, setShowFixedSpendDetails] = useState(false);
    const baseMonthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const getOrderedMonthNames = (startMonth) => {
        const monthNames = [...baseMonthNames];
        const rotationIndex = (startMonth - 1) % 12;

        const rotatedMonths = [...monthNames.slice(rotationIndex), ...monthNames.slice(0, rotationIndex)];

        return [...rotatedMonths, 'YTG', 'FY'];
    };

    const currentYear = new Date().getFullYear();
    const currentCalendarMonth = new Date().getMonth() + 1; // 1-12
    const selectedYear = selectedForecast?.startDate ? new Date(selectedForecast.startDate).getFullYear() : currentYear;
    const totalPeriods = aggregationPeriod === "monthly" ? 12 : (data?.baseUnits?.length - 1 || 12);
    
    const showYTG = !isForecastInPast(selectedYear, currentYear, data?.currentMonth, totalPeriods, startMonth, currentCalendarMonth);

    // Use period names from API response if available, otherwise fall back to month names
    const rawPeriodNames = data?.periodNames || getOrderedMonthNames(startMonth);
    // Build period names array: always filter out YTG/FY, then add them back
    // YTG is conditionally rendered in the body, so include it in array only when showing
    let basePeriodNames = [...(rawPeriodNames.filter(name => name !== 'YTG' && name !== 'FY'))];
    if (showYTG) {
        basePeriodNames.push('YTG');
    }
    basePeriodNames.push('FY');
    
    // Format period names with week numbers if it's weekly aggregation
    const periodNames = aggregationPeriod === "weekly" 
        ? basePeriodNames.map((period, index) => {
            // Check if this looks like a date (DD-MM-YYYY or DD-MM-YY format) 
            if (/^\d{2}-\d{2}-\d{2,4}$/.test(period)) {
                return formatWeekHeader(period, index);
            }
            return period; // Keep YTG, FY, etc. as is
        })
        : basePeriodNames;

    const addCommas = (value) => {
        return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    };

    const formatCurrency = (value) => {
        const numericValue = typeof value === 'number' ? value : Number(value);
        if (!Number.isFinite(numericValue)) {
            return 'N/A';
        }
        let formattedValue = Math.round(numericValue * 100) / 100;
        formattedValue = formattedValue % 1 === 0 ? formattedValue.toString() : formattedValue.toFixed(2);
        return `£${addCommas(formattedValue)}`;
    };

    const formatPercentage = (value) => {
        const numericValue = typeof value === 'number' ? value : Number(value);
        if (!Number.isFinite(numericValue)) {
            return 'N/A';
        }
        let formattedValue = Math.round(numericValue * 100) / 100;
        formattedValue = formattedValue % 1 === 0 ? formattedValue.toString() : formattedValue.toFixed(2);
        return `${addCommas(formattedValue)}%`;
    };

    const formatValue = (key, value) => {
        if (value === undefined || value === null) return 'N/A';

        if (key === 'grossProfitPercent') {
            return formatPercentage(value);
        } else if (['grossRevenue', 'triggers', 'fixedSpend', 'netRevenue', 'cogs', 'logs', 'grossProfit'].includes(key)) {
            return formatCurrency(value);
        } else {
            const numericValue = typeof value === 'number' ? value : Number(value);
            if (!Number.isFinite(numericValue)) {
                return 'N/A';
            }
            return addCommas(numericValue.toLocaleString());
        }
    };

    const formatVariance = (key, value) => {
        if (value === null || value === undefined) {
            return 'N/A';
        }
        const formatted = formatValue(key, value);
        return value > 0 ? `+${formatted}` : formatted;
    };

    const getActualSeriesForKey = (key) => {
        if (!showActuals) {
            return null;
        }
        if (key === 'grossRevenue') {
            return data?.actualGrossRevenue;
        }
        if (key === 'totalUnits') {
            return data?.actualTotalUnits;
        }
        if (key === 'triggers') {
            return data?.actualTriggers;
        }
        if (key === 'cogs') {
            return data?.actualCogs;
        }
        if (key === 'logs') {
            return data?.actualLogs;
        }
        return null;
    };

    const getActualValue = (key, index) => {
        const series = getActualSeriesForKey(key);
        if (!Array.isArray(series)) {
            return null;
        }
        const value = series[index];
        return value === undefined ? null : value;
    };

    const renderValueCell = (key, forecastValue, actualValue, periodIndex = null) => {
        const supportsActual = key === 'grossRevenue' || key === 'totalUnits' || key === 'triggers' || key === 'cogs' || key === 'logs';
        if (!showActuals || !supportsActual) {
            return formatValue(key, forecastValue);
        }

        const isLoading = loadingActuals;
        const forecastIsNumber = typeof forecastValue === 'number' && !Number.isNaN(forecastValue);
        const safeForecast = forecastIsNumber ? forecastValue : 0;
        const hasActual = actualValue !== null && actualValue !== undefined;
        const variance = hasActual && forecastIsNumber ? actualValue - safeForecast : null;
        
        const isPast = periodIndex !== null && isPeriodInPast(
            periodIndex,
            data?.periodNames || rawPeriodNames,
            startMonth,
            selectedYear,
            aggregationPeriod
        );

        if (!showCompare) {
            const displayValue = hasActual ? actualValue : (isPast ? null : forecastValue);
            return (
                <Typography 
                    variant="body2"
                    sx={{ 
                        color: hasActual ? 'primary.main' : 'inherit',
                        fontWeight: hasActual ? 'bold' : 'normal'
                    }}
                >
                    {isLoading ? '…' : (displayValue !== null ? formatValue(key, displayValue) : '—')}
                </Typography>
            );
        }

        const actualDisplay = isLoading
            ? '…'
            : hasActual
                ? formatValue(key, actualValue)
                : (isPast ? '—' : '—');
        const varianceDisplay = isLoading
            ? '…'
            : variance !== null
                ? formatVariance(key, variance)
                : '—';
        const varianceColor = variance === null || isLoading
            ? 'text.secondary'
            : variance > 0
                ? 'success.main'
                : variance < 0
                    ? 'error.main'
                    : 'text.secondary';

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.2 }}>
                <Typography
                    variant="caption"
                    component="span"
                    color="text.secondary"
                    sx={{ display: 'block', whiteSpace: 'nowrap', fontSize: '0.9rem' }}
                >
                    {formatValue(key, forecastValue)}
                </Typography>
                <Typography 
                    variant="body2"
                    sx={{ color: 'primary.main', fontWeight: 'bold' }}
                >
                    {actualDisplay}
                </Typography>
                <Typography
                    variant="caption"
                    component="span"
                    sx={{ display: 'block', whiteSpace: 'nowrap', color: varianceColor }}
                >
                    {varianceDisplay}
                </Typography>
            </Box>
        );
    };



    const copyToClipboard = async () => {
        await copyAccountTableToClipboard(data, startMonth, selectedYear, currentYear, aggregationPeriod, showFixedSpendDetails, showActuals);
    };

    const calculateTotalUnits = (data) => {
        if (!data.baseUnits || !data.promoUnits) return [];
        return data.baseUnits.map((baseUnit, index) => baseUnit + (data.promoUnits[index] || 0));
    };

    const handleToggleClick = () => {
        setShowDetailedUnits(prev => !prev);
    };

    const handleFixedSpendToggleClick = () => {
        setShowFixedSpendDetails(prev => !prev);
    };

    const renderRows = (data) => {
        const rows = [];
        const totalUnits = calculateTotalUnits(data);
        
        // Calculate number of periods (excluding FY total)
        const periodCount = (data.baseUnits || []).length - 1;
        const fyIndex = periodCount;
        
        // Add Total Units row
        if (totalUnits.length > 0) {
            const ytgValue = calculateYTG(totalUnits, data.currentMonth, selectedYear, currentYear, aggregationPeriod, startMonth, currentCalendarMonth);
            const fyValue = totalUnits[fyIndex];
            
            rows.push(
                <TableRow key="totalUnits" sx={{ height: 40 }}>
                    <TableCell 
                        component="th" 
                        scope="row" 
                        sx={{ 
                            height: 40, 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap', 
                            cursor: 'pointer',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)'
                            }
                        }}
                        onClick={handleToggleClick}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
                            Total Units
                            {showDetailedUnits ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </Box>
                    </TableCell>
                    {totalUnits.slice(0, periodCount).map((value, index) => (
                        <TableCell
                            key={index}
                            align="right"
                            sx={{
                                height: 40,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {renderValueCell('totalUnits', value, getActualValue('totalUnits', index), index)}
                        </TableCell>
                    ))}
                    {showYTG && (
                        <TableCell
                            key="ytg"
                            align="right"
                            sx={{
                                height: 40,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                fontWeight: 'bold'
                            }}
                        >
                            {renderValueCell('totalUnits', ytgValue, null, null)}
                        </TableCell>
                    )}
                    {/* FY Column */}
                    <TableCell
                        key="fy"
                        align="right"
                        sx={{
                            height: 40,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontWeight: 'bold'
                        }}
                    >
                        {renderValueCell('totalUnits', fyValue, getActualValue('totalUnits', fyIndex), fyIndex)}
                    </TableCell>
                </TableRow>
            );
        }

        // Add other rows
        Object.keys(data).forEach((key) => {
            // ✅ Skip non-array fields like 'startMonth', 'currentMonth', and the fixedSpendPromotions array
            if (!Array.isArray(data[key])) return;
            
            if (key === 'periodNames' || key === 'fixedSpendPromotions') return;
            if (key === 'actualGrossRevenue' || key === 'actualTotalUnits' || key === 'actualTriggers' || key === 'actualCogs' || key === 'actualLogs') return;
            
            if (!showDetailedUnits && (key === 'baseUnits' || key === 'promoUnits')) return;

            const ytgValue = key === 'grossProfitPercent' 
                ? calculateYTGPercentage(data.grossProfit, data.netRevenue, data.currentMonth, selectedYear, currentYear, aggregationPeriod, startMonth, currentCalendarMonth)
                : calculateYTG(data[key], data.currentMonth, selectedYear, currentYear, aggregationPeriod, startMonth, currentCalendarMonth);
            const fyValue = data[key][fyIndex];

            const isUnitsRow = key === 'baseUnits' || key === 'promoUnits';
            const isFixedSpendRow = key === 'fixedSpend';
            const hasPromotions = isFixedSpendRow && data.fixedSpendPromotions && data.fixedSpendPromotions.length > 0;
            const labelText = key.replace(/([A-Z])/g, ' $1').trim().replace(/^./, str => str.toUpperCase());
            
            rows.push(
                <TableRow key={key} sx={{ height: 40 }}>
                    <TableCell 
                        component="th" 
                        scope="row" 
                        sx={{ 
                            height: 40, 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap',
                            paddingLeft: isUnitsRow ? 3 : 2,
                            cursor: hasPromotions ? 'pointer' : 'default',
                            '&:hover': hasPromotions ? {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)'
                            } : {}
                        }}
                        onClick={hasPromotions ? handleFixedSpendToggleClick : undefined}
                    >
                        {hasPromotions ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
                                {labelText}
                                {showFixedSpendDetails ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </Box>
                        ) : (
                            labelText
                        )}
                    </TableCell>
                    {data[key].slice(0, periodCount).map((value, index) => (
                        <TableCell
                            key={index}
                            align="right"
                            sx={{
                                height: 40,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {renderValueCell(key, value, getActualValue(key, index), index)}
                        </TableCell>
                    ))}
                    {showYTG && (
                        <TableCell
                            key="ytg"
                            align="right"
                            sx={{
                                height: 40,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                fontWeight: 'bold'
                            }}
                        >
                            {renderValueCell(key, ytgValue, null, null)}
                        </TableCell>
                    )}
                    {/* FY Column */}
                    <TableCell
                        key="fy"
                        align="right"
                        sx={{
                            height: 40,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontWeight: 'bold'
                        }}
                    >
                        {renderValueCell(key, fyValue, getActualValue(key, fyIndex), fyIndex)}
                    </TableCell>
                </TableRow>
            );

            if (isFixedSpendRow && showFixedSpendDetails && data.fixedSpendPromotions) {
                data.fixedSpendPromotions.forEach((promo) => {
                    const promoYtgValue = calculateYTG(promo.fixedSpendValues, data.currentMonth, selectedYear, currentYear, aggregationPeriod, startMonth, currentCalendarMonth);
                    const promoFyValue = promo.fixedSpendValues[fyIndex];
                    
                    rows.push(
                        <TableRow key={`promo-${promo.promotionId}`} sx={{ height: 40 }}>
                            <TableCell 
                                component="th" 
                                scope="row" 
                                sx={{ 
                                    height: 40, 
                                    overflow: 'hidden', 
                                    textOverflow: 'ellipsis', 
                                    whiteSpace: 'nowrap',
                                    paddingLeft: 4,
                                    maxWidth: 200
                                }}
                                title={promo.promotionName}
                            >
                                {promo.promotionName}
                            </TableCell>
                            {promo.fixedSpendValues.slice(0, periodCount).map((value, index) => (
                                <TableCell
                                    key={index}
                                    align="right"
                                    sx={{
                                        height: 40,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {formatValue('fixedSpend', value)}
                                </TableCell>
                            ))}
                            {showYTG && (
                                <TableCell
                                    key="ytg"
                                    align="right"
                                    sx={{
                                        height: 40,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    {formatValue('fixedSpend', promoYtgValue)}
                                </TableCell>
                            )}
                            {/* FY Column */}
                            <TableCell
                                key="fy"
                                align="right"
                                sx={{
                                    height: 40,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    fontWeight: 'bold'
                                }}
                            >
                                {formatValue('fixedSpend', promoFyValue)}
                            </TableCell>
                        </TableRow>
                    );
                });
            }
        });

        return rows;
    };


    return (
        <TableContainer 
            component={Paper} 
            sx={{ 
                height: '100%',
                overflow: 'auto',
                maxWidth: '100%',
                display: 'inline-block',
                width: 'fit-content',
                minWidth: '100%'
            }}
        >
            <Table 
                aria-label="account summary table" 
                stickyHeader
                sx={{ 
                    tableLayout: 'auto', 
                    width: 'auto',
                    '& .MuiTableBody-root .MuiTableCell-root': {
                        whiteSpace: 'nowrap'
                    },
                    '& .MuiTableHead-root .MuiTableCell-root': {
                        whiteSpace: 'nowrap'
                    },
                    '& .MuiTableHead-root .MuiTableCell-root[data-week-header="true"]': {
                        whiteSpace: 'pre'
                    }
                }}
            >
                <TableHead>
                    <TableRow sx={{ height: 40 }}>
                        <TableCell sx={{ 
                            height: 40,
                            backgroundColor: (theme) => theme.palette.background.paper,
                            zIndex: 1
                        }}>
                            <Tooltip title="Copy table to clipboard">
                                <IconButton
                                    onClick={copyToClipboard}
                                    size="small"
                                    sx={{ p: 0.5 }}
                                >
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </TableCell>
                        {periodNames.map((period, index) => {
                            const isWeekHeader = aggregationPeriod === "weekly" && isDatePeriod(period);
                            return (
                                <TableCell
                                    key={index}
                                    align="right"
                                    sx={{
                                        height: 40,
                                        fontWeight: index >= periodNames.length - 2 ? 'bold' : 'normal',
                                        whiteSpace: isWeekHeader ? 'pre' : 'nowrap',
                                        backgroundColor: (theme) => theme.palette.background.paper,
                                        zIndex: 1
                                    }}
                                    data-week-header={isWeekHeader}
                                >
                                    {period.toUpperCase()}
                                </TableCell>
                            );
                        })}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data ? renderRows(data) : (
                        <TableRow sx={{ height: 40 }}>
                            <TableCell colSpan={periodNames.length + 1} sx={{ height: 40 }}>Loading...</TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default OutputsAccountTable;
